import type { SignerProfile, SigningField } from '../types/fields';

export const AUDIT_MANIFEST_STORAGE_KEY = 'local-pdf-signer:audit-manifests';

export interface SigningAuditManifest {
  version: 1;
  createdAt: string;
  app: 'local-pdf-signer';
  documentName: string;
  signedFileId: string;
  signerProfile: SignerProfile;
  fieldCount: number;
  fields: Array<{
    id: string;
    type: string;
    pageIndex: number;
    valuePreview?: string;
    hasImage: boolean;
  }>;
  signedSha256: string;
}

async function sha256Hex(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function fieldSummary(field: SigningField): SigningAuditManifest['fields'][number] {
  const value = field.value?.trim();
  return {
    id: field.id,
    type: field.type,
    pageIndex: field.pageIndex,
    valuePreview: value ? (value.length > 48 ? `${value.slice(0, 45)}…` : value) : undefined,
    hasImage: !!field.imageDataUrl,
  };
}

export async function buildSigningAuditManifest(params: {
  documentName: string;
  signedFileId: string;
  signedDownloadUrl: string;
  signerProfile: SignerProfile;
  fields: SigningField[];
  /** Optional: pass the already-downloaded signed PDF bytes to avoid a second fetch. */
  signedBytes?: ArrayBuffer;
  /** Optional AbortSignal forwarded to the re-fetch (ignored when signedBytes provided). */
  signal?: AbortSignal;
}): Promise<SigningAuditManifest> {
  // FX-C6 Fix 6: prefer pre-fetched bytes when available to avoid a redundant
  // round-trip; fall back to a fetch that respects the caller's AbortSignal and
  // degrades gracefully (placeholder hash) when the temp file is already gone.
  let signedSha256: string;
  if (params.signedBytes) {
    signedSha256 = await sha256Hex(params.signedBytes);
  } else {
    try {
      const res = await fetch(params.signedDownloadUrl, { signal: params.signal });
      if (!res.ok) {
        // Non-fatal: signing already succeeded; record a placeholder and move on.
        signedSha256 = 'unavailable';
      } else {
        signedSha256 = await sha256Hex(await res.arrayBuffer());
      }
    } catch {
      // Fetch aborted or temp file gone — signing still succeeded.
      signedSha256 = 'unavailable';
    }
  }

  return {
    version: 1,
    createdAt: new Date().toISOString(),
    app: 'local-pdf-signer',
    documentName: params.documentName,
    signedFileId: params.signedFileId,
    signerProfile: params.signerProfile,
    fieldCount: params.fields.length,
    fields: params.fields.map(fieldSummary),
    signedSha256,
  };
}

export function persistAuditManifest(manifest: SigningAuditManifest): void {
  try {
    const raw = localStorage.getItem(AUDIT_MANIFEST_STORAGE_KEY);
    const list: SigningAuditManifest[] = raw ? JSON.parse(raw) : [];
    const next = [manifest, ...list.filter((m) => m.signedFileId !== manifest.signedFileId)].slice(0, 50);
    localStorage.setItem(AUDIT_MANIFEST_STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Non-fatal — signing still succeeded
  }
}

export function downloadAuditManifest(manifest: SigningAuditManifest): void {
  const base = manifest.documentName.replace(/\.pdf$/i, '') || 'document';
  const stamp = manifest.createdAt.slice(0, 19).replace(/[:T]/g, '-');
  const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${base}-audit-${stamp}.json`;
  a.click();
  URL.revokeObjectURL(url);
}